import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { fetchEnquiriesForTenant } from '../../services/pocketbase';
import { normalizeSourceChannelForDisplay } from '../../config/constants';
import { format, subDays, subMonths } from 'date-fns';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiTrendingUp, FiCalendar, FiBarChart3 } = FiIcons;

const getThemeColor = (variable) => {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
};

const Dashboard = () => {
  const { getEffectiveTenantId } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, [getEffectiveTenantId()]);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const tenantId = getEffectiveTenantId();
      if (tenantId) {
        const enquiriesData = await fetchEnquiriesForTenant(tenantId);
        setEnquiries(enquiriesData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const chartTheme = {
    primary: getThemeColor('--color-primary'),
    accent: getThemeColor('--color-accent'),
    text: getThemeColor('--color-text'),
    surface: getThemeColor('--color-surface'),
  };

  // Metrics
  const totalEnquiries = enquiries.length;
  const last7Days = enquiries.filter(e => new Date(e.created) >= subDays(new Date(), 7)).length;
  const last30Days = enquiries.filter(e => new Date(e.created) >= subDays(new Date(), 30)).length;
  const convertedCount = enquiries.filter(e => e.status === 'converted').length;
  const conversionRate = totalEnquiries > 0 ? Math.round((convertedCount / totalEnquiries) * 100) : 0;

  // Snapshot Counts
  const snapshotCounts = {
    new: enquiries.filter(e => e.status === 'new').length,
    in_progress: enquiries.filter(e => e.status === 'in_progress').length,
    contacted: enquiries.filter(e => e.status === 'contacted').length,
    converted: enquiries.filter(e => e.status === 'converted').length,
    lost: enquiries.filter(e => e.status === 'lost').length,
  };

  // --- CHART DATA PREPARATION ---

  // 1. Status Breakdown (Bar Chart)
  const getStatusData = () => {
    const statuses = ['new', 'in_progress', 'contacted', 'converted', 'lost'];
    const counts = statuses.map(status => {
      return enquiries.filter(e => e.status === status).length;
    });

    const labels = statuses.map(s => s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
    return { labels, counts };
  };

  const statusChartOption = {
    title: { text: 'Enquiries by Status', left: 'center', textStyle: { color: chartTheme.text } },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: getStatusData().labels,
      axisLabel: { interval: 0, rotate: 30, color: chartTheme.text },
      axisLine: { lineStyle: { color: chartTheme.text, opacity: 0.15 } }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: chartTheme.text },
      splitLine: { lineStyle: { color: chartTheme.text, opacity: 0.1 } }
    },
    series: [{
      data: getStatusData().counts,
      type: 'bar',
      itemStyle: { color: chartTheme.primary },
      label: { show: true, position: 'top', fontWeight: 'bold', color: chartTheme.text }
    }],
    grid: { bottom: '15%', containLabel: true }
  };

  // 2. Source Breakdown (Pie Chart)
  const getSourceData = () => {
    const sourceCounts = {};
    enquiries.forEach(e => {
      const norm = normalizeSourceChannelForDisplay(e.source_channel);
      sourceCounts[norm] = (sourceCounts[norm] || 0) + 1;
    });

    return Object.keys(sourceCounts)
      .map(source => ({ value: sourceCounts[source], name: source }))
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        itemStyle: {
          color: index % 2 === 0 ? chartTheme.primary : chartTheme.accent,
          opacity: Math.max(0.5, 1 - (Math.floor(index / 2) * 0.12))
        }
      }));
  };

  const sourceChartOption = {
    title: { text: 'Enquiries by Source', left: 'center', textStyle: { color: chartTheme.text } },
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: '0%', left: 'center', textStyle: { color: chartTheme.text } },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 5,
        borderColor: chartTheme.surface,
        borderWidth: 2
      },
      label: {
        show: true,
        color: chartTheme.text,
        formatter: '{b}: {c} ({d}%)'
      },
      emphasis: {
        label: { show: true, fontSize: '16', fontWeight: 'bold', color: chartTheme.text }
      },
      data: getSourceData()
    }]
  };

  // 3. Enquiries Over Time (Line Chart - Monthly)
  const getTimelineData = () => {
    const months = [];
    const counts = [];

    for (let i = 5; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const monthKey = format(d, 'yyyy-MM');
      const monthLabel = format(d, 'MMM yyyy');

      const count = enquiries.filter(e => {
        return e.created.startsWith(monthKey);
      }).length;

      months.push(monthLabel);
      counts.push(count);
    }
    return { months, counts };
  };

  const timelineData = getTimelineData();
  const timelineChartOption = {
    title: { text: 'Enquiry Trend (6 Months)', left: 'center', textStyle: { color: chartTheme.text } },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: timelineData.months,
      axisLabel: { color: chartTheme.text },
      axisLine: { lineStyle: { color: chartTheme.text, opacity: 0.15 } }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: chartTheme.text },
      splitLine: { lineStyle: { color: chartTheme.text, opacity: 0.1 } }
    },
    series: [{
      data: timelineData.counts,
      type: 'line',
      smooth: true,
      label: { show: true, position: 'top', color: chartTheme.text },
      areaStyle: { color: chartTheme.primary, opacity: 0.14 },
      lineStyle: { color: chartTheme.primary },
      itemStyle: { color: chartTheme.primary }
    }],
    grid: { containLabel: true }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6 w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-200 h-80 rounded-lg"></div>
            <div className="bg-gray-200 h-80 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Enquiries</p>
                <p className="text-2xl font-bold text-gray-900">{totalEnquiries}</p>
              </div>
              <SafeIcon icon={FiMail} className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Last 7 Days</p>
                <p className="text-2xl font-bold text-gray-900">{last7Days}</p>
              </div>
              <SafeIcon icon={FiCalendar} className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Last 30 Days</p>
                <p className="text-2xl font-bold text-gray-900">{last30Days}</p>
              </div>
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-purple-500" />
            </div>
          </motion.div>

          <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
              </div>
              <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-orange-500" />
            </div>
          </motion.div>
        </div>

        {/* Snapshot Counts Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {Object.entries(snapshotCounts).map(([status, count]) => (
            <div key={status} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                {status.replace('_', ' ')}
              </p>
              <p className="text-xl font-bold text-gray-900">{count}</p>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            {totalEnquiries > 0 ? (
              <ReactECharts option={statusChartOption} style={{ height: '350px' }} />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-gray-400">No data available</div>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            {totalEnquiries > 0 ? (
              <ReactECharts option={sourceChartOption} style={{ height: '350px' }} />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-gray-400">No data available</div>
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 lg:col-span-2">
            {totalEnquiries > 0 ? (
              <ReactECharts option={timelineChartOption} style={{ height: '350px' }} />
            ) : (
              <div className="flex items-center justify-center h-[350px] text-gray-400">No data available</div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard;